import { gql, useApolloClient, useMutation } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconMarkdownSolid from '@apollo/icons/default/IconMarkdownSolid.svg';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Textarea,
} from '@apollo/orbit';
import * as monacoEditor from 'monaco-editor';
import React, { Fragment, useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { GraphRef, graphRefToString } from 'src/app/graph/hooks/useGraphRef';
import { proposalEditorRouteConfig } from 'src/app/graph/routes';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';

import { ProposalSchemaInfo } from '../hooks/useProposalEditorState/useProposalEditorState';
import { ProposalBuildInputFragment } from '../shared';

export const PublishRevisionModal = ({
  proposalId,
  isOpen,
  onClose,
  changedSubgraphsWithMetadata,
  graphRef,
  setInitialLatestRevisionId,
  isStale,
  latestLaunchId,
  setSaveRevisionDisabled,
  showPublishRevisionToast,
  updateChangedSubgraphsTextPostPublish,
  changedSubgraphsWithSyntaxErrors,
  subgraphsHaveChanges,
  canEditProposal,
  isMergeRevision,
  sourceGraphVariant,
  sourceCompositionId,
}: {
  proposalId: string;
  isOpen: boolean;
  onClose: () => void;
  changedSubgraphsWithMetadata: [string, ProposalSchemaInfo][];
  graphRef: GraphRef;
  setInitialLatestRevisionId: (id: string) => void;
  isStale: boolean;
  latestLaunchId: string;
  setSaveRevisionDisabled: (disabled: boolean) => void;
  showPublishRevisionToast: (
    isSuccessful: boolean,
    errorMessage: string | undefined,
  ) => void;
  updateChangedSubgraphsTextPostPublish: (
    newSubgraphHashes: Record<string, string>,
  ) => Promise<void>;
  canEditProposal: boolean;
  subgraphsHaveChanges: boolean;
  changedSubgraphsWithSyntaxErrors: [
    string,
    monacoEditor.editor.IMarkerData[],
  ][];
  isMergeRevision: boolean;
  sourceGraphVariant: string | undefined;
  sourceCompositionId: string | undefined;
}) => {
  const history = useHistory();
  const location = useLocation();

  const [currentAccountId] = useCurrentAccountId();
  const [summary, setSummary] = useState(
    isMergeRevision
      ? `Merge changes from ${graphRef.graphId}@${sourceGraphVariant}`
      : '',
  );

  const [publishSubgraphs] = useMutation<
    GraphQLTypes.PublishProposalRevisionMutation,
    GraphQLTypes.PublishProposalRevisionMutationVariables
  >(
    gql`
      # 🚨IMPORTANT🚨: Do not rename this query. We use it in our Proposals SLO by name.
      mutation PublishProposalRevisionMutation(
        $proposalId: ID!
        $input: PublishProposalSubgraphsInput!
        $graphVariant: String!
      ) {
        proposal(id: $proposalId) {
          ... on ProposalMutation {
            publishSubgraphs(input: $input) {
              ... on PermissionError {
                message
              }
              ... on ValidationError {
                message
              }
              ... on NotFoundError {
                message
              }
              ... on SchemaValidationError {
                message
              }
              ... on Proposal {
                id
                latestRevision {
                  id
                }
                backingVariant {
                  id
                  graph {
                    id
                    mostRecentCompositionPublish(graphVariant: $graphVariant) {
                      graphCompositionID
                    }
                  }
                  activeSchemaPublish {
                    id
                  }
                  ...ProposalBuildInputFragment
                }
              }
            }
          }
        }
      }
      ${ProposalBuildInputFragment}
    `,
    { errorPolicy: 'all' },
  );
  const client = useApolloClient();
  const handleOnClose = useCallback(() => {
    setSummary('');
    onClose();
    setSaveRevisionDisabled(false);
  }, [onClose, setSaveRevisionDisabled]);

  const publishSubgraphsCall = useCallback(async () => {
    const subgraphInputs = [];
    for (const [, subgraph] of changedSubgraphsWithMetadata) {
      subgraphInputs.push({
        name: subgraph.id,
        activePartialSchema: {
          sdl: subgraph.text,
        },
        ...(typeof subgraph.initialText !== 'string' ? { url: '' } : {}),
      });
    }

    const result = await publishSubgraphs({
      variables: {
        graphVariant: graphRef.graphVariant,
        proposalId,
        input: {
          subgraphInputs,
          summary,
          revision: `studio-proposals-${currentAccountId}-${Date.now()}`,
          previousLaunchId: latestLaunchId,
          mergeUpdateCompositionId: isMergeRevision
            ? sourceCompositionId
            : undefined,
        },
      },
    });

    let errorMessage = result.errors?.[0]?.message;
    let isSuccessful = false;

    const proposal = result.data?.proposal;
    if (proposal) {
      switch (proposal.__typename) {
        case 'NotFoundError':
        case 'PermissionError':
        case 'ValidationError':
          break;
        case 'ProposalMutation': {
          switch (proposal.publishSubgraphs.__typename) {
            case 'NotFoundError':
            case 'PermissionError':
            case 'ValidationError':
            case 'SchemaValidationError':
              errorMessage = proposal.publishSubgraphs.message.includes(
                'is not the most recent launch for this proposal',
              )
                ? 'This proposal has been updated since you started editing. Please refresh and republish.'
                : proposal.publishSubgraphs.message;
              break;
            case 'Proposal': {
              setInitialLatestRevisionId(
                proposal.publishSubgraphs.latestRevision.id,
              );
              isSuccessful = true;
              break;
            }
            default:
              assertUnreachable(proposal.publishSubgraphs);
          }
          break;
        }
        default:
          assertUnreachable(proposal);
      }
    }
    const proposalMutation =
      proposal?.__typename === 'ProposalMutation' ? proposal : undefined;
    const updatedBuildInput =
      proposalMutation?.publishSubgraphs.__typename === 'Proposal'
        ? proposalMutation.publishSubgraphs.backingVariant.latestLaunch
            ?.buildInput.__typename === 'CompositionBuildInput'
          ? proposalMutation.publishSubgraphs.backingVariant.latestLaunch
              ?.buildInput
          : undefined
        : undefined;
    const updatedSubgraphHashes = Object.fromEntries(
      updatedBuildInput?.subgraphs.map((subgraph) => [
        subgraph.name,
        subgraph.hash,
      ]) ?? [],
    );
    await updateChangedSubgraphsTextPostPublish(updatedSubgraphHashes);
    setSaveRevisionDisabled(false);

    client.cache.evict({
      id: client.cache.identify({
        __typename: 'GraphVariant',
        id: graphRefToString(graphRef),
      }),
      fieldName: 'latestLaunch',
    });
    client.cache.evict({
      id: client.cache.identify({
        __typename: 'Service',
        id: graphRef.graphId,
      }),
      fieldName: 'checkWorkflows',
    });
    client.cache.evict({
      id: client.cache.identify({
        __typename: 'Proposal',
        id: proposalId,
      }),
      fieldName: 'rebaseConflicts',
    });

    setSummary('');
    showPublishRevisionToast(isSuccessful, errorMessage);
  }, [
    publishSubgraphs,
    graphRef,
    proposalId,
    summary,
    currentAccountId,
    latestLaunchId,
    isMergeRevision,
    sourceCompositionId,
    updateChangedSubgraphsTextPostPublish,
    setSaveRevisionDisabled,
    client.cache,
    showPublishRevisionToast,
    changedSubgraphsWithMetadata,
    setInitialLatestRevisionId,
  ]);

  const onSubmit = useCallback(async () => {
    setSaveRevisionDisabled(true);
    publishSubgraphsCall();
    onClose();
    history.push(
      proposalEditorRouteConfig.locationFrom({
        location,
        patch: {
          pullUpstreamChanges: RouteConfig.DELETE,
        },
      }),
    );
  }, [
    setSaveRevisionDisabled,
    publishSubgraphsCall,
    history,
    location,
    onClose,
  ]);

  const saveButtonDisabled =
    !canEditProposal ||
    (!subgraphsHaveChanges && !isMergeRevision) ||
    isStale ||
    isMinLengthArray(1, changedSubgraphsWithSyntaxErrors);

  return (
    <Modal size="xl" isOpen={isOpen} onClose={handleOnClose} autoFocus={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Save a revision of your proposal</ModalTitle>
          <ModalDescription>
            Revisions give your team context into schema changes made and the
            rationale behind them.
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          <FormControl>
            <FormLabel>
              Summary of your schema changes (Optional){' '}
              {/* TODO(proposals-checks): Add Checks to the list here */}
              <Tooltip
                placement="top"
                label="This description will appear on your activity timeline and Changes page."
              >
                <IconInfo tabIndex={0} />
              </Tooltip>
            </FormLabel>
            <div className="relative">
              <Textarea
                size="lg"
                name="summary"
                placeholder="Give your team some details about the schema changes you’ll be requesting."
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                className="resize-y"
                autoFocus
              />
              <IconMarkdownSolid className="absolute bottom-3 right-3 z-10 text-icon-primary" />
            </div>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" onClick={handleOnClose}>
              Cancel
            </Button>
            <Tooltip
              label={
                !subgraphsHaveChanges
                  ? `No changes to save`
                  : isStale
                    ? `Your local schema is out of date. Try refreshing the page.`
                    : isMinLengthArray(1, changedSubgraphsWithSyntaxErrors)
                      ? changedSubgraphsWithSyntaxErrors.map(
                          ([subgraph, errors]) => (
                            <Fragment key={subgraph}>
                              <div>{subgraph} has invalid SDL:</div>
                              {errors.map((error) => (
                                <div
                                  key={`${error.startLineNumber}${error.startColumn}${error.message}`}
                                >
                                  ln:{error.startLineNumber} - {error.message}
                                </div>
                              ))}
                            </Fragment>
                          ),
                        )
                      : null
              }
              disabled={!saveButtonDisabled}
              placement="right"
              shouldWrapChildren={true}
            >
              <Button
                // 🚨 Do not delete or change this data-testid / this button without updating the Proposals Datadog Browser test
                // TODO(Maya) add link to synthetics test
                data-testid="proposals_save_revision_button_modal"
                variant="primary"
                type="submit"
                onClick={onSubmit}
                isDisabled={saveButtonDisabled}
              >
                Save Revision
              </Button>
            </Tooltip>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
